import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hourglass, Loader2 } from 'lucide-react';
import { bookingsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatDate } from '../../lib/utils';

const TONE = { not_started: 'bg-gray-100 text-gray-700', free: 'bg-success-100 text-success-700', accruing: 'bg-amber-100 text-amber-900', stopped: 'bg-blue-100 text-blue-700' };
const LABEL = { not_started: 'Not started', free: 'Within free time', accruing: 'Accruing', stopped: 'Clock stopped' };

function money(v) { return formatCurrency(v); }

function Clock({ title, clock, kind, count, testId }) {
  if (!clock) return null;
  const hasAmount = typeof clock.total === 'number';
  let headline, detail;
  if (clock.status === 'not_started') {
    headline = kind === 'demurrage' ? 'Starts on arrival' : 'Starts at gate-out';
    detail = clock.basis === 'eta' ? 'Projected from ETA ' + formatDate(clock.clock_start) + ' — awaiting arrival confirmation · last free day would be ' + formatDate(clock.last_free_day) : 'Expected ' + formatDate(clock.clock_start) + ' · last free day ' + formatDate(clock.last_free_day);
  } else if (clock.status === 'free') {
    headline = clock.days_left + (clock.days_left === 1 ? ' free day left' : ' free days left');
    detail = 'Free time started ' + formatDate(clock.clock_start) + (clock.basis === 'eta' ? ' (ETA)' : '') + ' · last free day ' + formatDate(clock.last_free_day) + (kind === 'detention' ? ' — return the empty by then' : '');
  } else if (clock.status === 'accruing') {
    headline = clock.days_over + (clock.days_over === 1 ? ' day over free time' : ' days over free time');
    detail = 'Free time ended ' + formatDate(clock.last_free_day) + ' · ' + (kind === 'demurrage' ? 'demurrage' : 'detention') + ' accruing daily';
  } else {
    headline = 'Stopped ' + formatDate(clock.stopped_at);
    detail = clock.chargeable_days ? clock.chargeable_days + ' chargeable day(s) beyond ' + formatDate(clock.last_free_day) : 'Collected within free time — no charge';
  }
  return (
    <div className="py-3 first:pt-0 last:pb-0" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-ink">{title}</p>
        <span className={"text-[11px] font-medium uppercase tracking-[0.04em] px-2 py-0.5 rounded-full " + TONE[clock.status]} data-testid={testId + '-status'}>{LABEL[clock.status]}</span>
      </div>
      <p className={"text-xl mt-1 " + (clock.status === 'accruing' ? 'text-amber-900' : 'text-ink')} data-testid={testId + '-headline'}>{headline}</p>
      <p className="text-[13px] text-gray-600 mt-0.5">{detail}</p>
      {hasAmount && clock.total > 0 && (
        <p className="text-sm text-ink mt-1.5 sv-mono" data-testid={testId + '-amount'}>{money(clock.total)} accrued{count > 1 ? ' · ' + money(clock.per_container) + ' × ' + count : ''}{kind === 'demurrage' && clock.tier2_days ? ' (' + clock.tier1_days + ' d @ ' + money(clock.rate_tier1) + ' + ' + clock.tier2_days + ' d @ ' + money(clock.rate_tier2) + ')' : ''}</p>
      )}
    </div>
  );
}

// Free-time clock and demurrage/detention position. Amounts come from the authenticated endpoint; public callers pass the tracking payload.
export function FreeTimeCard({ bookingId, publicData, className = '', testId = 'free-time-card' }) {
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(publicData || null);
  const [loading, setLoading] = useState(false);

  useEffect(function () {
    let alive = true;
    setData(publicData || null);
    if (!bookingId || !isAuthenticated) return undefined;
    setLoading(true);
    bookingsApi.demurrage(bookingId).then(function (r) { if (alive) setData(r.data); }).catch(function () { /* keep public view */ }).finally(function () { if (alive) setLoading(false); });
    return function () { alive = false; };
  }, [bookingId, isAuthenticated, publicData]);

  if (!data && !loading) return null;
  const priced = Boolean(data && data.tariff);
  return (
    <div className={"bg-white rounded-sm shadow-card p-5 " + className} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Hourglass className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
        <h3 className="font-medium text-ink text-[15px]">Free time & demurrage</h3>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 ml-auto" />}
      </div>
      {data && !data.applicable && <p className="text-sm text-gray-600" data-testid={testId + '-na'}>{data.note}</p>}
      {data && data.applicable && (
        <div className="divide-y divide-gray-100">
          <Clock title="Demurrage · container at terminal" clock={data.demurrage} kind="demurrage" count={data.container_count} testId={testId + '-demurrage'} />
          <Clock title="Detention · empty return" clock={data.detention} kind="detention" count={data.container_count} testId={testId + '-detention'} />
          {priced && (
            <div className="pt-3 text-[12px] text-gray-600" data-testid={testId + '-tariff'}>
              {data.total_accrued > 0 && <p className="text-sm text-ink mb-1">Total accrued <span className="sv-mono font-medium" data-testid={testId + '-total'}>{money(data.total_accrued)}</span> as of {formatDate(data.as_of)}</p>}
              Tariff per {data.container_type_label}: {data.tariff.demurrage_free_days} free days, then days 1–{data.tariff.demurrage_tier1_days} {money(data.tariff.demurrage_tier1)}/day and {money(data.tariff.demurrage_tier2)}/day thereafter · detention {data.tariff.detention_free_days} free days, then {money(data.tariff.detention_daily)}/day.
            </div>
          )}
          {!priced && !isAuthenticated && <p className="pt-3 text-[12px] text-gray-600" data-testid={testId + '-login-hint'}><Link to="/login" className="sv-link">Log in</Link> to see accrued charges and the tariff for this shipment.</p>}
        </div>
      )}
    </div>
  );
}
